import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { WarningCallout } from '../controls/WarningCallout'
import { MICRO_LABEL, MONO, TEXT_BODY_SM, TEXT_CAPTION } from '../recipes'
import { VOICE_SAMPLE_TARGET_SPEECH_MS, VOICE_TOPICS, type VoiceTopic } from './voice-rules'
import type { VoiceMicrophoneProblem } from './voice-types'

export interface VoiceRailProps {
  /** Why the last start gave no recording, said above the steps. */
  problem?: VoiceMicrophoneProblem | null
  /** The topic the recording shows now: marked in the list. */
  activeTopic?: VoiceTopic | null
  /** The recording is over: the steps stay to be read, set back. */
  quiet?: boolean
}

/** What the operator has the subject do, in the order it is done. */
const STEPS = ['distance', 'alone', 'amount'] as const

/**
 * The one entrance of the dialog: the lines come up one after the other, 60 ms apart. Under
 * reduced motion they are simply there.
 */
const ENTER =
  'animate-in fade-in-0 slide-in-from-bottom-2 duration-300 fill-mode-both motion-reduce:animate-none'
const ENTER_DELAY = ['delay-0', 'delay-60', 'delay-120', 'delay-180'] as const

/** One numbered step on the rail: the number in a ring on the line that joins the steps. */
function Step({ index, last, children }: { index: number; last: boolean; children: string }) {
  return (
    <li className={cn('relative flex gap-3 pb-4 last:pb-0', ENTER, ENTER_DELAY[index])}>
      {!last && <span aria-hidden className="absolute top-7 bottom-0 left-3.5 w-px bg-border" />}
      <span
        aria-hidden
        className={cn(
          MONO,
          'relative flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-popover text-xs font-semibold text-primary-strong'
        )}
      >
        {index + 1}
      </span>
      <span className={cn(TEXT_BODY_SM, 'pt-0.5 leading-6')}>{children}</span>
    </li>
  )
}

function Topics({ activeTopic }: Pick<VoiceRailProps, 'activeTopic'>) {
  const { t } = useTranslation()
  return (
    <section className={cn('flex flex-col gap-2', ENTER, ENTER_DELAY[STEPS.length])}>
      <h3 className={MICRO_LABEL}>{t('humint.voice.sample.rail.topics')}</h3>
      <p className={cn(TEXT_CAPTION, 'text-muted-foreground')}>
        {t('humint.voice.sample.rail.topicsHint')}
      </p>
      <ul className="flex flex-col gap-1.5">
        {VOICE_TOPICS.map(topic => (
          <li
            key={topic}
            aria-current={topic === activeTopic ? 'true' : undefined}
            className={cn(
              TEXT_BODY_SM,
              'group flex items-baseline gap-2.5 leading-6 transition-colors aria-[current=true]:font-medium',
              activeTopic && topic !== activeTopic && 'text-muted-foreground'
            )}
          >
            <span
              aria-hidden
              className="size-1.5 shrink-0 -translate-y-0.5 rounded-full bg-border transition-colors group-aria-[current=true]:bg-primary"
            />
            {t(`humint.voice.sample.topics.${topic}`)}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * The instructions of a voice sample as a numbered rail (ADR-0062): three short lines on what
 * the subject does — the phone near the mouth in a quiet place, the subject alone and without
 * stopping, thirty seconds of speech with the pauses left out — and what to talk about: everyday
 * things, never the case. It is written for the operator, who says it to the subject. Memoised:
 * the recorder's ticks do not draw it again.
 */
export const VoiceRail = memo(function VoiceRail({
  problem = null,
  activeTopic = null,
  quiet = false,
}: VoiceRailProps) {
  const { t } = useTranslation()
  const seconds = VOICE_SAMPLE_TARGET_SPEECH_MS / 1000
  return (
    <div
      className={cn(
        'flex flex-col gap-6 transition-colors duration-300 motion-reduce:transition-none',
        quiet && 'text-muted-foreground'
      )}
    >
      {problem && <WarningCallout>{t(`humint.voice.sample.microphone.${problem}`)}</WarningCallout>}
      <section className="flex flex-col gap-3">
        <h3 className={MICRO_LABEL}>{t('humint.voice.sample.rail.before')}</h3>
        <ol className="flex flex-col">
          {STEPS.map((step, index) => (
            <Step key={step} index={index} last={index === STEPS.length - 1}>
              {t(`humint.voice.sample.rail.steps.${step}`, { seconds })}
            </Step>
          ))}
        </ol>
      </section>
      <Topics activeTopic={activeTopic} />
    </div>
  )
})
